import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axiosInstance from "../../../api/axiosInstance";

const PAGE_SIZE = 10;
const TOAST_DURATION = 4000;
const CMS_TABS = [
  { key: "pages", label: "Page Master" },
  { key: "page_types", label: "Page Type Master" },
  { key: "section_types", label: "Page Section Master" },
];

const pagesApi = {
  list: (params = {}) => axiosInstance.get("/api/superadmin/pages/", { params }),
  remove: (id) => axiosInstance.delete(`/api/superadmin/pages/${id}/`),
  publish: (id) => axiosInstance.post(`/api/superadmin/pages/${id}/publish/`),
  unpublish: (id) => axiosInstance.post(`/api/superadmin/pages/${id}/unpublish/`),
  duplicate: (id) => axiosInstance.post(`/api/superadmin/pages/${id}/duplicate/`),
};

const pageTypesApi = {
  list: () => axiosInstance.get("/api/superadmin/page-types/"),
  create: (body) => axiosInstance.post("/api/superadmin/page-types/", body),
  update: (id, body) => axiosInstance.put(`/api/superadmin/page-types/${id}/`, body),
  remove: (id) => axiosInstance.delete(`/api/superadmin/page-types/${id}/`),
};

const pageSectionTypesApi = {
  list: () => axiosInstance.get("/api/superadmin/page-section-types/"),
  create: (body) => axiosInstance.post("/api/superadmin/page-section-types/", body),
  update: (id, body) => axiosInstance.put(`/api/superadmin/page-section-types/${id}/`, body),
  remove: (id) => axiosInstance.delete(`/api/superadmin/page-section-types/${id}/`),
};

let _toastSetter = null;
const toast = {
  _push: (item) => {
    const id = Date.now() + Math.random();
    _toastSetter?.((p) => [...p, { id, ...item }]);
    return id;
  },
  success: (title, msg) => toast._push({ type: "success", title, msg }),
  error: (title, msg) => toast._push({ type: "error", title, msg }),
  info: (title, msg) => toast._push({ type: "info", title, msg }),
  loading: (title) => toast._push({ type: "loading", title, persistent: true }),
  dismiss: (id) => _toastSetter?.((p) => p.filter((t) => t.id !== id)),
};

const TOAST_META = {
  success: { bar: "#16a34a", icon: "OK" },
  error: { bar: "#dc2626", icon: "!" },
  info: { bar: "#1d4ed8", icon: "i" },
  loading: { bar: "#94a3b8", icon: null },
};

const PlusIcon = () => (<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>);
const EditIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>);
const SearchIcon = () => (<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>);
const CloseIcon = () => (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>);
const ChevronLeftIcon = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><polyline points="15 18 9 12 15 6" /></svg>);
const ChevronRightIcon = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><polyline points="9 18 15 12 9 6" /></svg>);

function ToastItem({ t, onDismiss }) {
  const meta = TOAST_META[t.type];
  const [progress, setProgress] = useState(100);
  const [exiting, setExiting] = useState(false);
  const rafRef = useRef(null);
  const startTs = useRef(Date.now());

  const dismiss = useCallback(() => {
    setExiting(true);
    setTimeout(() => onDismiss(t.id), 280);
  }, [t.id, onDismiss]);

  useEffect(() => {
    if (t.persistent) return undefined;
    const tick = () => {
      const pct = Math.max(0, 100 - ((Date.now() - startTs.current) / TOAST_DURATION) * 100);
      setProgress(pct);
      if (pct > 0) rafRef.current = requestAnimationFrame(tick);
      else dismiss();
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [t.persistent, dismiss]);

  return (
    <div style={{ position: "relative", overflow: "hidden", background: "#fff", borderRadius: 10, borderLeft: `4px solid ${meta.bar}`, boxShadow: "0 4px 20px rgba(0,0,0,0.10), 0 0 0 1px rgba(0,0,0,0.06)", minWidth: 300, maxWidth: 360, animation: exiting ? "toastOut 0.28s ease forwards" : "toastIn 0.28s cubic-bezier(0.34,1.56,0.64,1) forwards" }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "13px 14px 14px" }}>
        <div style={{ flexShrink: 0, width: 24, height: 24, borderRadius: "50%", background: `${meta.bar}18`, display: "flex", alignItems: "center", justifyContent: "center", marginTop: 1 }}>
          {t.type === "loading" ? (
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ animation: "spin 0.85s linear infinite" }}>
              <circle cx="6.5" cy="6.5" r="5" stroke={meta.bar} strokeWidth="2" strokeDasharray="8 20" strokeLinecap="round" />
            </svg>
          ) : (
            <span style={{ fontSize: 10, fontWeight: 800, color: meta.bar, lineHeight: 1 }}>{meta.icon}</span>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 13.5, fontWeight: 650, color: "#0f172a", lineHeight: 1.35 }}>{t.title}</p>
          {t.msg && <p style={{ margin: "3px 0 0", fontSize: 12.5, color: "#64748b", lineHeight: 1.5 }}>{t.msg}</p>}
        </div>
        <button onClick={dismiss} style={{ background: "none", border: "none", cursor: "pointer", color: "#cbd5e1", padding: 2, flexShrink: 0, display: "flex", borderRadius: 4, marginTop: 1 }}>
          <CloseIcon />
        </button>
      </div>
      {!t.persistent && <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, background: "#f1f5f9" }}><div style={{ height: "100%", width: `${progress}%`, background: meta.bar, transition: "width 0.1s linear" }} /></div>}
    </div>
  );
}

function ToastContainer() {
  const [toasts, setToasts] = useState([]);
  _toastSetter = setToasts;
  const dismiss = useCallback((id) => setToasts((p) => p.filter((t) => t.id !== id)), []);
  if (!toasts.length) return null;
  return <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 9999, display: "flex", flexDirection: "column", gap: 10, alignItems: "flex-end" }}>{toasts.map((t) => <ToastItem key={t.id} t={t} onDismiss={dismiss} />)}</div>;
}

function Modal({ title, onClose, children }) {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = ""; document.removeEventListener("keydown", onKey); };
  }, [onClose]);

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 520, boxShadow: "0 24px 64px rgba(0,0,0,0.18)", animation: "cmsFadeUp 0.2s ease" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 24px", borderBottom: "1px solid #f1f5f9" }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: "#0f172a" }}>{title}</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#94a3b8", cursor: "pointer", display: "flex", padding: 4 }}><CloseIcon /></button>
        </div>
        <div style={{ padding: "20px 24px 24px" }}>{children}</div>
      </div>
    </div>
  );
}

const normalizeSlug = (value) => String(value || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
const formatOptionLabel = (value) => String(value || "").replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
const getResults = (data) => Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
const getApiMessage = (err, fallback) => {
  const data = err?.response?.data;
  if (!data) return fallback;
  if (typeof data === "string") return data;
  if (data.detail) return data.detail;
  if (data.message) return data.message;
  const first = Object.values(data).flat().find(Boolean);
  return first ? String(first) : fallback;
};

function Badge({ active }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600, ...(active ? { background: "#dcfce7", color: "#16a34a" } : { background: "#fee2e2", color: "#dc2626" }) }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: active ? "#22c55e" : "#f43f5e" }} />
      {active ? "Active" : "Inactive"}
    </span>
  );
}

function StatusBadge({ value }) {
  const styles = {
    published: { bg: "#dcfce7", color: "#16a34a", dot: "#22c55e" },
    draft: { bg: "#f1f5f9", color: "#475569", dot: "#94a3b8" },
    archived: { bg: "#fee2e2", color: "#dc2626", dot: "#f43f5e" },
  };
  const s = styles[value] || styles.draft;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600, background: s.bg, color: s.color }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: s.dot }} />
      {formatOptionLabel(value)}
    </span>
  );
}

function SkeletonRows({ columns }) {
  return Array.from({ length: PAGE_SIZE }).map((_, row) => (
    <tr key={row} style={{ borderBottom: "1px solid #f1f5f9" }}>
      {Array.from({ length: columns }).map((__, col) => (
        <td key={col} style={{ padding: "14px 16px" }}>
          <div style={{ height: 13, width: col === 0 ? 32 : col === columns - 1 ? 120 : 150, maxWidth: "100%", borderRadius: 6, background: "linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)", backgroundSize: "200% 100%", animation: "cmsShimmer 1.4s infinite" }} />
        </td>
      ))}
    </tr>
  ));
}

function Pagination({ currentPage, totalPages, totalCount, onPageChange }) {
  if (totalPages <= 1) return null;
  const startItem = (currentPage - 1) * PAGE_SIZE + 1;
  const endItem = Math.min(currentPage * PAGE_SIZE, totalCount);
  const btnBase = { display: "inline-flex", alignItems: "center", justifyContent: "center", minWidth: 34, height: 34, padding: "0 6px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: "pointer", background: "#fff", color: "#374151", lineHeight: 1 };
  const pages = Array.from({ length: totalPages }, (_, i) => i + 1);
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px", borderTop: "1px solid #f1f5f9", background: "#f8fafc", flexWrap: "wrap", gap: 10 }}>
      <span style={{ fontSize: 13, color: "#64748b" }}>Showing <strong style={{ color: "#374151" }}>{startItem}-{endItem}</strong> of <strong style={{ color: "#374151" }}>{totalCount}</strong> results</span>
      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
        <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} style={{ ...btnBase, opacity: currentPage === 1 ? 0.4 : 1, cursor: currentPage === 1 ? "not-allowed" : "pointer" }}><ChevronLeftIcon /></button>
        {pages.map((p) => (
          <button key={p} onClick={() => onPageChange(p)} className={p === currentPage ? "" : "cms-page-btn"} style={{ ...btnBase, ...(p === currentPage ? { background: "#3b82f6", color: "#fff", border: "1px solid #3b82f6", fontWeight: 600 } : {}) }}>{p}</button>
        ))}
        <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} style={{ ...btnBase, opacity: currentPage === totalPages ? 0.4 : 1, cursor: currentPage === totalPages ? "not-allowed" : "pointer" }}><ChevronRightIcon /></button>
      </div>
    </div>
  );
}

function StatsBar({ data, loading, totalLabel = "Total", activeLabel = "Active", inactiveLabel = "Inactive" }) {
  const total = data.length;
  const active = data.filter((d) => d.is_active !== false).length;
  const inactive = total - active;
  const Card = ({ label, count, dotColor, bg, border }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 18px", borderRadius: 10, background: bg, border: `1px solid ${border}`, minWidth: 110 }}>
      <div style={{ width: 8, height: 8, borderRadius: "50%", background: dotColor, flexShrink: 0 }} />
      <div>
        <div style={{ fontSize: 18, fontWeight: 700, color: "#0f172a", lineHeight: 1.1 }}>{loading ? <div style={{ width: 28, height: 16, borderRadius: 4, background: "#e2e8f0", animation: "cmsShimmer 1.4s infinite", backgroundSize: "200% 100%" }} /> : count}</div>
        <div style={{ fontSize: 11.5, color: "#64748b", fontWeight: 550, marginTop: 2 }}>{label}</div>
      </div>
    </div>
  );
  return (
    <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
      <Card label={totalLabel} count={total} dotColor="#6366f1" bg="#f5f3ff" border="#e0e7ff" />
      <Card label={activeLabel} count={active} dotColor="#16a34a" bg="#f0fdf4" border="#bbf7d0" />
      <Card label={inactiveLabel} count={inactive} dotColor="#dc2626" bg="#fef2f2" border="#fecaca" />
    </div>
  );
}

function MasterForm({ kind, item, loading, onCancel, onSubmit }) {
  const [form, setForm] = useState({
    name: item?.name || "",
    description: item?.description || "",
    sort_order: item?.sort_order ?? 0,
    is_active: item?.is_active !== false,
  });
  const [errors, setErrors] = useState({});
  const isEdit = !!item?.id;
  const slug = normalizeSlug(form.name);
  const inputStyle = (key) => ({ width: "100%", padding: "9px 12px", border: `1.5px solid ${errors[key] ? "#f43f5e" : "#e2e8f0"}`, borderRadius: 8, fontSize: 14, color: "#1e293b", outline: "none", background: errors[key] ? "#fff1f2" : "#fff", boxSizing: "border-box", fontFamily: "inherit" });
  const labelStyle = { fontSize: 13, fontWeight: 600, color: "#374151" };

  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: "" }));
  };

  const submit = () => {
    const nextErrors = {};
    if (!form.name.trim()) nextErrors.name = `${kind} name is required.`;
    if (form.sort_order === "" || Number.isNaN(Number(form.sort_order))) nextErrors.sort_order = "Enter a valid number.";
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }
    onSubmit({ ...form, name: form.name.trim(), slug, sort_order: Number(form.sort_order) || 0 }, setErrors);
  };

  return (
    <div>
      {errors.__global && <div style={{ background: "#fff5f5", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", marginBottom: 14 }}><p style={{ margin: 0, fontSize: 13, color: "#dc2626", fontWeight: 550 }}>{errors.__global}</p></div>}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 150px", gap: 14 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          <label style={{ ...labelStyle, color: errors.name ? "#f43f5e" : "#374151" }}>{kind} Name <span style={{ color: "#f43f5e" }}>*</span></label>
          <input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder={`e.g. ${kind}`} style={inputStyle("name")} />
          {errors.name && <span style={{ fontSize: 12, color: "#f43f5e" }}>{errors.name}</span>}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          <label style={{ ...labelStyle, color: errors.sort_order ? "#f43f5e" : "#374151" }}>Sort Order <span style={{ color: "#f43f5e" }}>*</span></label>
          <input type="number" value={form.sort_order} onChange={(e) => set("sort_order", e.target.value)} placeholder="0" style={inputStyle("sort_order")} />
          {errors.sort_order && <span style={{ fontSize: 12, color: "#f43f5e" }}>{errors.sort_order}</span>}
        </div>
        <div style={{ gridColumn: "1 / -1", display: "flex", flexDirection: "column", gap: 5 }}>
          <label style={labelStyle}>Slug</label>
          <div style={{ padding: "9px 12px", border: "1.5px solid #e2e8f0", borderRadius: 8, background: "#f8fafc", fontSize: 13, color: "#64748b" }}><code>{slug || "auto_generated"}</code></div>
        </div>
        <div style={{ gridColumn: "1 / -1", display: "flex", flexDirection: "column", gap: 5 }}>
          <label style={labelStyle}>Description</label>
          <textarea value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Description" rows={3} style={{ ...inputStyle("description"), resize: "vertical" }} />
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 600, color: "#374151" }}>
          <input type="checkbox" checked={form.is_active} onChange={(e) => set("is_active", e.target.checked)} /> Active
        </label>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 22 }}>
        <button onClick={onCancel} disabled={loading} style={{ padding: "9px 20px", border: "1px solid #e2e8f0", borderRadius: 8, background: "#fff", color: "#64748b", fontSize: 14, fontWeight: 600, cursor: "pointer", opacity: loading ? 0.6 : 1 }}>Cancel</button>
        <button onClick={submit} disabled={loading} style={{ padding: "9px 22px", border: "none", borderRadius: 8, background: "#3b82f6", color: "#fff", fontSize: 14, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.7 : 1 }}>{loading ? "Saving..." : isEdit ? `Update ${kind}` : `Add ${kind}`}</button>
      </div>
    </div>
  );
}

function DeleteConfirm({ item, label, loading, onCancel, onConfirm }) {
  const displayName = item.name || item.title || "this item";
  return (
    <div style={{ textAlign: "center", padding: "4px 0 0" }}>
      <p style={{ fontSize: 16, fontWeight: 600, color: "#0f172a", margin: "0 0 6px" }}>Delete <span style={{ color: "#f43f5e" }}>"{displayName}"</span>?</p>
      <p style={{ fontSize: 13, color: "#94a3b8", margin: "0 0 24px" }}>If this {label.toLowerCase()} is already used, it will be marked inactive.</p>
      <div style={{ display: "flex", justifyContent: "center", gap: 10 }}>
        <button onClick={onCancel} disabled={loading} style={{ padding: "9px 20px", border: "1px solid #e2e8f0", borderRadius: 8, background: "#fff", color: "#64748b", fontSize: 14, fontWeight: 600, cursor: "pointer", opacity: loading ? 0.6 : 1 }}>Cancel</button>
        <button onClick={onConfirm} disabled={loading} style={{ padding: "9px 22px", border: "none", borderRadius: 8, background: "#f43f5e", color: "#fff", fontSize: 14, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.7 : 1 }}>Delete</button>
      </div>
    </div>
  );
}

function MasterRecordsTab({ title, subtitle, addLabel, recordLabel, api, countKey }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showAdd, setShowAdd] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);

  useEffect(() => { setCurrentPage(1); }, [search, activeFilter]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: response } = await api.list();
      setData(getResults(response).sort((a, b) => (b.is_active !== false ? 1 : 0) - (a.is_active !== false ? 1 : 0) || (a.sort_order || 0) - (b.sort_order || 0)));
    } catch (err) {
      toast.error("Failed to Load", getApiMessage(err, `Unable to fetch ${title.toLowerCase()}.`));
    } finally {
      setLoading(false);
    }
  }, [api, title]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return data.filter((item) => {
      const matchesSearch = !term || [item.name, item.slug, item.description].some((value) => String(value || "").toLowerCase().includes(term));
      const matchesStatus = activeFilter === "" || String(item.is_active !== false) === activeFilter;
      return matchesSearch && matchesStatus;
    });
  }, [data, search, activeFilter]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pagedData = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const inputBase = { padding: "8px 12px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 14, background: "#fff", color: "#1e293b", outline: "none", fontFamily: "inherit" };
  const columns = ["Sr No.", "Name", "Slug", "Status", "Sort Order", "Used In", "Actions"];

  const applyFieldErrors = (payload, setErrors) => {
    if (!payload || typeof payload !== "object") return false;
    const mapped = {};
    Object.keys(payload).forEach((key) => {
      const value = payload[key];
      mapped[key === "non_field_errors" ? "__global" : key] = Array.isArray(value) ? value[0] : String(value);
    });
    if (!Object.keys(mapped).length) return false;
    setErrors(mapped);
    return true;
  };

  const save = async (body, setErrors) => {
    setActionLoading(true);
    const tid = toast.loading(`${editItem ? "Updating" : "Creating"} ${recordLabel.toLowerCase()}...`);
    try {
      if (editItem) await api.update(editItem.id, body);
      else await api.create(body);
      toast.dismiss(tid);
      toast.success("Saved", `${recordLabel} ${editItem ? "updated" : "created"} successfully.`);
      setShowAdd(false);
      setEditItem(null);
      fetchData();
    } catch (err) {
      toast.dismiss(tid);
      if (!applyFieldErrors(err?.response?.data, setErrors)) toast.error("Save Failed", getApiMessage(err, `Could not save ${recordLabel.toLowerCase()}.`));
    } finally {
      setActionLoading(false);
    }
  };

  const remove = async () => {
    if (!deleteItem) return;
    setActionLoading(true);
    const tid = toast.loading(`Deleting "${deleteItem.name}"...`);
    try {
      await api.remove(deleteItem.id);
      toast.dismiss(tid);
      toast.success("Deleted", `${recordLabel} deleted or deactivated successfully.`);
      setDeleteItem(null);
      fetchData();
    } catch (err) {
      toast.dismiss(tid);
      toast.error("Delete Failed", getApiMessage(err, `Could not delete ${recordLabel.toLowerCase()}.`));
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#0f172a" }}>{title}</h1>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "#64748b" }}>{subtitle}</p>
        </div>
        <button className="cms-add-btn" onClick={() => setShowAdd(true)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "9px 18px", background: "#3b82f6", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
          <PlusIcon /> {addLabel}
        </button>
      </div>

      <StatsBar data={data} loading={loading} totalLabel={`Total ${recordLabel}s`} />

      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <div style={{ position: "relative", flex: "1 1 240px" }}>
          <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", display: "flex", pointerEvents: "none" }}><SearchIcon /></span>
          <input style={{ ...inputBase, width: "100%", paddingLeft: 34 }} placeholder={`Search ${recordLabel.toLowerCase()}s...`} value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <select style={{ ...inputBase, cursor: "pointer" }} value={activeFilter} onChange={(e) => setActiveFilter(e.target.value)}>
          <option value="">All Status</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
        {(search || activeFilter) && <button onClick={() => { setSearch(""); setActiveFilter(""); }} style={{ ...inputBase, display: "flex", alignItems: "center", gap: 5, cursor: "pointer", color: "#dc2626", background: "#fff5f5", border: "1px solid #fecaca", fontWeight: 600 }}><CloseIcon /> Clear</button>}
      </div>

      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead>
              <tr>{columns.map((h) => <th key={h} style={{ padding: "11px 16px", textAlign: "left", fontSize: 12, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", background: "#f8fafc", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {loading ? <SkeletonRows columns={columns.length} /> : filtered.length === 0 ? (
                <tr><td colSpan={columns.length}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "56px 20px", gap: 8 }}>
                    <p style={{ margin: 0, fontSize: 16, fontWeight: 600, color: "#374151" }}>No {recordLabel.toLowerCase()}s found</p>
                    <p style={{ margin: 0, fontSize: 13, color: "#94a3b8" }}>{search || activeFilter ? "Try adjusting your search or filters" : `Add your first ${recordLabel.toLowerCase()} to get started`}</p>
                    {!search && !activeFilter && <button onClick={() => setShowAdd(true)} style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, padding: "8px 18px", background: "#3b82f6", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}><PlusIcon /> {addLabel}</button>}
                  </div>
                </td></tr>
              ) : pagedData.map((item, idx) => (
                <tr key={item.id} className="cms-row" style={{ borderBottom: "1px solid #f1f5f9" }}>
                  <td style={{ padding: "13px 16px", color: "#94a3b8", fontSize: 13 }}>{(currentPage - 1) * PAGE_SIZE + idx + 1}</td>
                  <td style={{ padding: "13px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 34, height: 34, borderRadius: 10, flexShrink: 0, background: `hsl(${((item.id || 1) * 47) % 360},70%,94%)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, color: `hsl(${((item.id || 1) * 47) % 360},60%,38%)` }}>{(item.name || "?").charAt(0).toUpperCase()}</div>
                      <span style={{ fontWeight: 600, color: "#0f172a", fontSize: 14 }}>{item.name}</span>
                    </div>
                  </td>
                  <td style={{ padding: "13px 16px", color: "#64748b", fontSize: 12 }}><code>{item.slug}</code></td>
                  <td style={{ padding: "13px 16px" }}><Badge active={item.is_active !== false} /></td>
                  <td style={{ padding: "13px 16px", color: "#64748b" }}>{item.sort_order ?? 0}</td>
                  <td style={{ padding: "13px 16px", color: "#64748b" }}>{item[countKey] || 0}</td>
                  <td style={{ padding: "13px 16px" }}>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button className="cms-edit-btn" title="Edit" onClick={() => setEditItem(item)} style={{ display: "flex", alignItems: "center", padding: "6px 10px", gap: 5, background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 6, cursor: "pointer", color: "#3b82f6", fontSize: 12, fontWeight: 600 }}><EditIcon /> Edit</button>
                      <button className="cms-del-btn" title="Delete" onClick={() => setDeleteItem(item)} style={{ display: "flex", alignItems: "center", padding: "6px 10px", gap: 5, background: "#fff1f2", border: "1px solid #fecdd3", borderRadius: 6, cursor: "pointer", color: "#f43f5e", fontSize: 12, fontWeight: 600 }}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loading && filtered.length > 0 && <Pagination currentPage={currentPage} totalPages={totalPages} totalCount={filtered.length} onPageChange={(p) => { if (p >= 1 && p <= totalPages) setCurrentPage(p); }} />}
      </div>

      {showAdd && <Modal title={addLabel} onClose={() => setShowAdd(false)}><MasterForm kind={recordLabel} loading={actionLoading} onCancel={() => setShowAdd(false)} onSubmit={save} /></Modal>}
      {editItem && <Modal title={`Edit ${recordLabel}`} onClose={() => setEditItem(null)}><MasterForm kind={recordLabel} item={editItem} loading={actionLoading} onCancel={() => setEditItem(null)} onSubmit={save} /></Modal>}
      {deleteItem && <Modal title={`Delete ${recordLabel}`} onClose={() => setDeleteItem(null)}><DeleteConfirm item={deleteItem} label={recordLabel} loading={actionLoading} onCancel={() => setDeleteItem(null)} onConfirm={remove} /></Modal>}
    </>
  );
}

function PageMasterTab({ fixedPageType, title = "Page Master", subtitle = "Manage dynamic home, landing, content, and case study pages" }) {
  const navigate = useNavigate();
  const returnTo = fixedPageType ? "/cms/case-studies" : "/cms/pages?tab=pages";
  const [pages, setPages] = useState([]);
  const [pageTypes, setPageTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState(fixedPageType || "");
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [acting, setActing] = useState(null);
  const [confirm, setConfirm] = useState(null);

  useEffect(() => { setTypeFilter(fixedPageType || ""); }, [fixedPageType]);
  useEffect(() => { setCurrentPage(1); }, [search, typeFilter, statusFilter]);

  const loadTypes = useCallback(async () => {
    try {
      const { data } = await pageTypesApi.list();
      setPageTypes(getResults(data));
    } catch (err) {
      toast.error("Load failed", getApiMessage(err, "Could not load page types."));
    }
  }, []);

  const loadPages = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.q = search.trim();
      if (fixedPageType || typeFilter) params.page_type = fixedPageType || typeFilter;
      if (statusFilter) params.status = statusFilter;
      const { data } = await pagesApi.list(params);
      setPages(getResults(data));
    } catch (err) {
      toast.error("Load failed", getApiMessage(err, "Could not load pages."));
    } finally {
      setLoading(false);
    }
  }, [fixedPageType, search, statusFilter, typeFilter]);

  useEffect(() => { loadTypes(); }, [loadTypes]);
  useEffect(() => { loadPages(); }, [loadPages]);

  const totalPages = Math.ceil(pages.length / PAGE_SIZE);
  const pagedPages = pages.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const inputBase = { padding: "8px 12px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 14, background: "#fff", color: "#1e293b", outline: "none", fontFamily: "inherit" };
  const columns = ["Sr No.", "Title", "Slug", "Type", "Status", "Sections", "Published At", "Actions"];

  const runAction = async (action, page) => {
    setActing(page.id);
    const tid = toast.loading(`${formatOptionLabel(action)} page...`);
    try {
      if (action === "deactivate") await pagesApi.remove(page.id);
      else if (action === "publish") await pagesApi.publish(page.id);
      else if (action === "unpublish") await pagesApi.unpublish(page.id);
      else if (action === "duplicate") await pagesApi.duplicate(page.id);
      toast.dismiss(tid);
      toast.success("Done", `Page ${action}d successfully.`);
      loadPages();
    } catch (err) {
      toast.dismiss(tid);
      toast.error("Action Failed", getApiMessage(err, "Action failed."));
    } finally {
      setActing(null);
      setConfirm(null);
    }
  };

  const askDelete = (page) => setConfirm(page);

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#0f172a" }}>{title}</h1>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "#64748b" }}>{subtitle}</p>
        </div>
        <button className="cms-add-btn" onClick={() => navigate("/cms/pages/new", { state: { returnTo } })} style={{ display: "flex", alignItems: "center", gap: 6, padding: "9px 18px", background: "#3b82f6", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
          <PlusIcon /> Add Page
        </button>
      </div>

      <StatsBar data={pages} loading={loading} totalLabel="Total Pages" activeLabel="Active" inactiveLabel="Inactive" />

      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <div style={{ position: "relative", flex: "1 1 240px" }}>
          <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", display: "flex", pointerEvents: "none" }}><SearchIcon /></span>
          <input style={{ ...inputBase, width: "100%", paddingLeft: 34 }} placeholder="Search pages..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        {!fixedPageType && (
          <select style={{ ...inputBase, cursor: "pointer" }} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="">All Types</option>
            {pageTypes.filter((type) => type.is_active !== false).map((type) => <option key={type.slug} value={type.slug}>{type.name}</option>)}
          </select>
        )}
        <select style={{ ...inputBase, cursor: "pointer" }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All Status</option>
          <option value="draft">Draft</option>
          <option value="published">Published</option>
          <option value="archived">Archived</option>
        </select>
        {(search || (!fixedPageType && typeFilter) || statusFilter) && <button onClick={() => { setSearch(""); if (!fixedPageType) setTypeFilter(""); setStatusFilter(""); }} style={{ ...inputBase, display: "flex", alignItems: "center", gap: 5, cursor: "pointer", color: "#dc2626", background: "#fff5f5", border: "1px solid #fecaca", fontWeight: 600 }}><CloseIcon /> Clear</button>}
      </div>

      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead><tr>{columns.map((h) => <th key={h} style={{ padding: "11px 16px", textAlign: "left", fontSize: 12, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", background: "#f8fafc", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>{h}</th>)}</tr></thead>
            <tbody>
              {loading ? <SkeletonRows columns={columns.length} /> : pages.length === 0 ? (
                <tr><td colSpan={columns.length}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "56px 20px", gap: 8 }}>
                    <p style={{ margin: 0, fontSize: 16, fontWeight: 600, color: "#374151" }}>No pages found</p>
                    <p style={{ margin: 0, fontSize: 13, color: "#94a3b8" }}>Add your first page to get started</p>
                    <button onClick={() => navigate("/cms/pages/new", { state: { returnTo } })} style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, padding: "8px 18px", background: "#3b82f6", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}><PlusIcon /> Add Page</button>
                  </div>
                </td></tr>
              ) : pagedPages.map((page, idx) => (
                <tr key={page.id} className="cms-row" style={{ borderBottom: "1px solid #f1f5f9" }}>
                  <td style={{ padding: "13px 16px", color: "#94a3b8", fontSize: 13 }}>{(currentPage - 1) * PAGE_SIZE + idx + 1}</td>
                  <td style={{ padding: "13px 16px", fontWeight: 600, color: "#0f172a" }}>{page.title}</td>
                  <td style={{ padding: "13px 16px", color: "#64748b", fontSize: 12 }}><code>{page.slug}</code></td>
                  <td style={{ padding: "13px 16px" }}><span style={{ display: "inline-flex", padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600, background: "#f1f5f9", color: "#475569" }}>{page.page_type_label || formatOptionLabel(page.page_type)}</span></td>
                  <td style={{ padding: "13px 16px" }}><StatusBadge value={page.status} /></td>
                  <td style={{ padding: "13px 16px", color: "#64748b" }}>{page.section_count || 0}</td>
                  <td style={{ padding: "13px 16px", color: "#94a3b8", fontSize: 12, whiteSpace: "nowrap" }}>{page.published_at ? new Date(page.published_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-"}</td>
                  <td style={{ padding: "13px 16px" }}>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <button disabled={acting === page.id} className="cms-edit-btn" onClick={() => navigate(`/cms/pages/${page.slug}`, { state: { returnTo } })} style={{ display: "flex", alignItems: "center", padding: "6px 10px", gap: 5, background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 6, cursor: "pointer", color: "#3b82f6", fontSize: 12, fontWeight: 600 }}><EditIcon /> Edit</button>
                      {page.status !== "published" && <button disabled={acting === page.id} onClick={() => runAction("publish", page)} style={{ display: "flex", alignItems: "center", padding: "6px 10px", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 6, cursor: "pointer", color: "#16a34a", fontSize: 12, fontWeight: 600 }}>Publish</button>}
                      {page.status === "published" && <button disabled={acting === page.id} onClick={() => runAction("unpublish", page)} style={{ display: "flex", alignItems: "center", padding: "6px 10px", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, cursor: "pointer", color: "#d97706", fontSize: 12, fontWeight: 600 }}>Unpublish</button>}
                      <button disabled={acting === page.id} onClick={() => runAction("duplicate", page)} style={{ display: "flex", alignItems: "center", padding: "6px 10px", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 6, cursor: "pointer", color: "#475569", fontSize: 12, fontWeight: 600 }}>Duplicate</button>
                      <button disabled={acting === page.id} className="cms-del-btn" onClick={() => askDelete(page)} style={{ display: "flex", alignItems: "center", padding: "6px 10px", background: "#fff1f2", border: "1px solid #fecdd3", borderRadius: 6, cursor: "pointer", color: "#f43f5e", fontSize: 12, fontWeight: 600 }}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loading && pages.length > 0 && <Pagination currentPage={currentPage} totalPages={totalPages} totalCount={pages.length} onPageChange={(p) => { if (p >= 1 && p <= totalPages) setCurrentPage(p); }} />}
      </div>

      {confirm && <Modal title="Delete Page" onClose={() => setConfirm(null)}><DeleteConfirm item={confirm} label="Page" loading={acting === confirm.id} onCancel={() => setConfirm(null)} onConfirm={() => runAction("deactivate", confirm)} /></Modal>}
    </>
  );
}

export default function PageMaster({ fixedPageType = "", title = "", subtitle = "" }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const activeTab = fixedPageType ? "pages" : CMS_TABS.some((tab) => tab.key === requestedTab) ? requestedTab : "pages";

  useEffect(() => {
    if (!fixedPageType && requestedTab && !CMS_TABS.some((tab) => tab.key === requestedTab)) {
      setSearchParams({ tab: "pages" }, { replace: true });
    }
  }, [fixedPageType, requestedTab, setSearchParams]);

  return (
    <div style={{ fontFamily: "'Inter', -apple-system, sans-serif", padding: 24, background: "#f8fafc", minHeight: "100vh", color: "#1e293b" }}>
      <style>{`
        @keyframes cmsShimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
        @keyframes cmsFadeUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes toastIn { from { opacity: 0; transform: translateX(24px) scale(0.97); } to { opacity: 1; transform: translateX(0) scale(1); } }
        @keyframes toastOut { from { opacity: 1; transform: translateX(0) scale(1); } to { opacity: 0; transform: translateX(28px) scale(0.95); } }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .cms-row:hover { background: #f8fafc !important; }
        .cms-edit-btn:hover { background: #dbeafe !important; }
        .cms-del-btn:hover { background: #ffe4e6 !important; }
        .cms-add-btn:hover { background: #2563eb !important; }
        .cms-page-btn:hover { background: #f1f5f9 !important; border-color: #cbd5e1 !important; }
        * { box-sizing: border-box; }
      `}</style>
      <ToastContainer />

      {!fixedPageType && (
        <div style={{ display: "flex", gap: 2, borderBottom: "1px solid #e2e8f0", marginBottom: 22, overflowX: "auto" }}>
          {CMS_TABS.map((tab) => (
            <button key={tab.key} onClick={() => setSearchParams({ tab: tab.key })} style={{ padding: "10px 18px", background: "none", border: "none", borderBottom: `2px solid ${activeTab === tab.key ? "#3b82f6" : "transparent"}`, color: activeTab === tab.key ? "#2563eb" : "#64748b", fontSize: 13.5, fontWeight: activeTab === tab.key ? 700 : 550, cursor: "pointer", marginBottom: -1, whiteSpace: "nowrap" }}>
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {activeTab === "page_types" && !fixedPageType && (
        <MasterRecordsTab
          title="Page Type Master"
          subtitle="Manage CMS page type master data"
          addLabel="Add Page Type"
          recordLabel="Page Type"
          api={pageTypesApi}
          countKey="page_count"
        />
      )}

      {activeTab === "section_types" && !fixedPageType && (
        <MasterRecordsTab
          title="Page Section Master"
          subtitle="Manage CMS page section type master data"
          addLabel="Add Page Section"
          recordLabel="Page Section"
          api={pageSectionTypesApi}
          countKey="section_count"
        />
      )}

      {activeTab === "pages" && (
        <PageMasterTab
          fixedPageType={fixedPageType}
          title={fixedPageType && title ? title : "Page Master"}
          subtitle={fixedPageType && subtitle ? subtitle : "Manage dynamic home, landing, content, and case study pages"}
        />
      )}
    </div>
  );
}
